import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { assetUrl } from '../api/assets';
import { HatchlingPortrait } from '../companion/HatchlingPortrait';
import { me, meKeys } from '../api/auth';
import { fetchSlots } from '../api/abilities';
import { useCompanions, useSetActive, type CompanionDto } from '../api/companions';
import { useWardrobe } from '../api/wardrobe';
import { useCareAction } from '../api/care';
import { useWash } from '../api/wash';
import { useOnline } from '../pwa/useOnline';
import { BottomSheet, Dialog } from '../components/BottomSheet';
import { LooksSheet } from '../looks/LooksSheet';
import { WardrobeScreenForCompanion } from '../wardrobe/WardrobeScreen';
import { CareMeters, lowCareCue } from '../habitat/CareMeters';
import { CareTray } from '../habitat/CareTray';
import { careItemError } from '../habitat/FeedSheet';
import { FoodPlaceLayer } from '../habitat/FoodPlaceLayer';
import { EAT_HOLD_MS, type PlaceFoodItem } from '../habitat/placeFood';
import { sfx } from '../habitat/sfx';
import {
  EMPTY_POKE_MOOD,
  makePokeBursts,
  notePoke,
  pickPokeAction,
  type PokeBurst,
  type PokeCss,
} from '../habitat/pokeReactions';
import {
  GROOM_JOY_MS, appendGroomSample, isOverActor, shouldCompleteGroom, type GroomSample,
} from '../habitat/groomShake';
import { advanceWash, initialWash, isWashTool, stainFadeFor } from '../habitat/washStages';
import { LevelRing } from '../habitat/LevelRing';
import { AllocateSheet } from '../habitat/AllocateSheet';
import { IdentityPanel } from '../habitat/IdentityPanel';
import { EquipmentSheet } from '../habitat/EquipmentSheet';
import { AbilitySlotsSheet } from '../habitat/AbilitySlotsSheet';
import { CompanionPickerSheet } from '../habitat/CompanionPickerSheet';
import { HabitatScene } from '../habitat/HabitatScene';
import { useHabitatWander } from '../habitat/useHabitatWander';
import { clientToStagePct, type Point } from '../habitat/wanderMath';
import { BubblesPlaylet } from '../habitat/playlets/BubblesPlaylet';
import { FetchPlaylet } from '../habitat/playlets/FetchPlaylet';
import { HideSeekPlaylet } from '../habitat/playlets/HideSeekPlaylet';
import { pickPlaylet, type PlayletCode } from '../habitat/playlets/pickPlaylet';
import { itemIconPath, type PetBaseVariant } from '../compositor/assetPaths';
import { formatEta } from '../lib/formatEta';
import { HabitatDockIcon } from '../habitat/dockIcons';
import { stainsAllowed, useGorePref } from '../fx/gorePrefs';
import { useCelebrationQueue } from '../shell/celebrationQueue';
import { useLayoutTier, isStudio } from '../shell/layoutTiers';
import { Studio } from '../shell/Studio';
import { Dashboard } from '../habitat/Dashboard';
import { TodayCard } from '../world/TodayCard';
import { NextStepCard } from '../quests/NextStepCard';
import { useCompanionSkills, useCancelSkill } from '../api/skills';
import { useFlag } from '../api/flags';
import { useNow } from '../world/clocks';
import { SkillsLine } from '../habitat/SkillsLine';
import { SkillsSheet } from '../habitat/SkillsSheet';

import { getSeenLevel, markLevelSeen } from '../shell/levelBaseline';
export { resetLevelBaselineForTests } from '../shell/levelBaseline';

// SCREENS.md /habitat "empty": the den fills the stage and the card sits on the floor just above the
// dock. It used to be an in-flow image with the card BELOW it, which put the only button at y=922 on
// an 839 screen, under the dock (onboarding 2026-09-23). No dead-end: links to /me.
function EmptyHabitat() {
  return (
    <main className="habitat habitat-empty-den">
      <img src={assetUrl('bd_habitat_default')} alt="" className="habitat-scene-bg" />
      <section className="habitat-empty">
        <div className="habitat-empty-peek" aria-hidden="true">
          <HatchlingPortrait code="murrow" />
          <HatchlingPortrait code="corvane" />
          <HatchlingPortrait code="brack" />
        </div>
        <h1>Your den is ready</h1>
        <p>Twelve companions are waiting to meet you. Pick one to move in.</p>
        <Link to="/companions/new" className="btn-primary">Choose your companion</Link>
        <Link to="/me" className="link">Meanwhile, your Keeper profile</Link>
      </section>
    </main>
  );
}

// SCREENS.md /habitat "loading": skeleton matching the full-bleed stage (backdrop + still
// compositor + overlay chrome), never a spinner. Exported so the gallery renders this exact
// markup instead of a hand-copied duplicate that could drift. Floor placeholder count must
// track the populated scene's .habitat-scene-floor children (HabitatScreen.test.tsx).
export function HabitatSkeleton() {
  return (
    <main className="habitat">
      <div className="habitat-scene" aria-hidden="true">
        <img
          className="habitat-scene-bg"
          src={assetUrl('bd_habitat_default')}
          alt=""
          width={1024}
          height={2216}
        />
        <div className="habitat-scene-top">
          <div className="skeleton skeleton-line" />
          <div className="skeleton" />
          <div className="skeleton" />
          <div className="skeleton" />
        </div>
        <div className="habitat-scene-actor" style={{ left: '44%', top: '78%' }}>
          <div className="compositor skeleton" />
        </div>
        <div className="habitat-scene-floor">
          <div className="skeleton" />
          <div className="skeleton" />
          <div className="skeleton" />
          <div className="skeleton" />
          <div className="skeleton" />
          <div className="skeleton" />
        </div>
      </div>
    </main>
  );
}

/**
 * Active companion owns the live scene (meters + allocate in `top`; Feed/Play/Dress/Equip/Looks
 * dock in `floor` — a right-hand rail, not a row on the den floor). Inactive companions are a compact name + Set active row — not in the room, not a
 * `.habitat-card`. Sheets stay on this owner so Task 7+ can pause roam without a card stack.
 *
 * On the desk (studio-desktop-layout Task 7, spec §4.5 / D2) the same two blocks are placed by
 * `<Studio>` instead: identity in the left panel (studio3) or the fold, the dock replaced by the
 * `Dashboard` in the right panel, and CareTray / CompanionPickerSheet rendering there as Asides.
 */
export function CompanionCard({ companion, companions = [companion], switchable = false }: {
  companion: CompanionDto;
  /** The account's whole list (the screen's own `useCompanions()` data), for the desk's identity
   *  panel switcher rows (Task 7b). Defaults to just this companion so every other caller and every
   *  pre-existing test is unchanged. */
  companions?: CompanionDto[];
  switchable?: boolean;
}) {
  const isOnline = useOnline();
  // D13 (2026-08-24, battle-gore-and-wash fix round 1): this companion's own permanent stains
  // are gated on the VIEWING account's age band, same `=== 'ADULT'` pattern (folding `UNKNOWN`
  // into the restricted side) as `BattleScreen`'s and `ProfileScreen`'s own gates — see either
  // for the fuller reasoning. `meKeys.detail` is the same `['me']` cache every other screen's
  // own `/me` read already shares, so this is usually a cache hit.
  const meQuery = useQuery({ queryKey: meKeys.detail, queryFn: me, retry: false });
  // FINDING 1 fix (final whole-branch review): D10 requires the gore toggle to hide DRAWN stains,
  // not just the battle burst — this screen used to gate on age band alone. `stainsAllowed` is
  // the shared derivation combining both, so this and the other three render sites cannot drift.
  const [goreOn] = useGorePref();
  const stainsOn = stainsAllowed(goreOn, meQuery.data?.ageBand);
  const wardrobe = useWardrobe(companion.id);
  const setActive = useSetActive();
  const careAction = useCareAction(companion.id);
  const washMutation = useWash(companion.id);
  const wander = useHabitatWander();
  const [showLooksSheet, setShowLooksSheet] = useState(false);
  const looksTriggerRef = useRef<HTMLButtonElement>(null);

  const [showCareTray, setShowCareTray] = useState(false);
  // Which tab the care tray opens on. The phone dock's Feed key always opens Food; the desk's
  // Dashboard (Task 7) opens Feed on Food and Groom on Groom — same tray, one prop.
  const [careTrayTab, setCareTrayTab] = useState<'food' | 'groom'>('food');
  const feedTriggerRef = useRef<HTMLButtonElement>(null);
  // The Dashboard's Groom card is a second entry into the same tray; the tray restores focus to
  // whichever of the two opened it (the phone dock has only the Feed key).
  const groomTriggerRef = useRef<HTMLButtonElement>(null);
  // Studio tier (spec §4.2): read once here, like BagScreen. jsdom reports 'phone', so every
  // pre-existing test below renders exactly the phone screen it always did.
  const tier = useLayoutTier();
  const studio = isStudio(tier);
  const three = tier === 'studio3';
  const [stageUpToast, setStageUpToast] = useState<string | null>(null);
  const [playError, setPlayError] = useState<string | null>(null);
  const [feedError, setFeedError] = useState<string | null>(null);
  const [placing, setPlacing] = useState<PlaceFoodItem | null>(null);
  const [foodDrop, setFoodDrop] = useState<{ code: string; pos: Point } | null>(null);
  const [ghostPos, setGhostPos] = useState<Point | null>(null);
  const [eatHold, setEatHold] = useState(false);
  const feedingRef = useRef(false);
  const placingRef = useRef<PlaceFoodItem | null>(null);
  placingRef.current = placing;
  const [grooming, setGrooming] = useState<string | null>(null);
  const [groomHold, setGroomHold] = useState(false);
  const [groomError, setGroomError] = useState<string | null>(null);
  const groomBusyRef = useRef(false);
  const groomSamplesRef = useRef<GroomSample[]>([]);
  const mutateCareRef = useRef(careAction.mutateAsync);
  mutateCareRef.current = careAction.mutateAsync;
  // The three-stage wash (spec 2026-08-23 §6.2) rides the SAME shake gesture as Groom (D4) —
  // `grooming` above still holds the armed item code regardless of whether it's a grooming item
  // or a wash tool. `wash` only tracks progress through the pure stage machine; D5: a wash tool
  // never posts a care action, so `washMutation` is called once, at the final stage.
  const [wash, setWash] = useState(initialWash);

  const [showAllocateSheet, setShowAllocateSheet] = useState(false);
  const allocateTriggerRef = useRef<HTMLButtonElement>(null);

  const [showEquipmentSheet, setShowEquipmentSheet] = useState(false);
  const equipTriggerRef = useRef<HTMLButtonElement>(null);
  const [showDressOverlay, setShowDressOverlay] = useState(false);
  const dressTriggerRef = useRef<HTMLButtonElement>(null);
  // The wardrobe's own guarded Close, for the overlay's Escape (onboard F24) — see WardrobeScreen.
  const dressCloseRef = useRef<(() => void) | null>(null);

  // Task 11 (fix-wave restructure): the slots sheet is a viewer + a route, never the place a
  // respec executes -- see `handleReplaceSlot` below for the full reasoning. Same
  // `['abilities', 'slots', companionId]` query key TrainingScreen.tsx already uses (and already
  // invalidates on `['abilities']` after a lesson completes), so this and Training School's own
  // cache never drift against each other.
  const [showAbilitiesSheet, setShowAbilitiesSheet] = useState(false);
  const abilitiesTriggerRef = useRef<HTMLButtonElement>(null);
  // 2026-09-14: the identity bar's name is the companion switcher on a multi-companion account —
  // see CompanionPickerSheet's header for why the old per-companion strips are gone.
  const [showPickerSheet, setShowPickerSheet] = useState(false);
  const pickerTriggerRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  // Task 7b: on the desk the left panel renders the six slot pips on load, so the fetch is justified
  // by rendered content there too — one query, one key, still nothing fetched on the phone until
  // the sheet opens.
  const slotsQuery = useQuery({
    queryKey: ['abilities', 'slots', companion.id],
    queryFn: () => fetchSlots(companion.id),
    enabled: showAbilitiesSheet || three,
  });
  const skillsQuery = useCompanionSkills(companion.id);
  const cancelSkill = useCancelSkill();
  const now = useNow();
  const skillCraft = useFlag('skill_craft');
  // #14.1 (CLAUDE.md "Hide the empty"): the wardrobe is parked until its art exists — Dress and
  // Looks (phone dock + Dashboard) render only once this is on.
  const wardrobeOn = useFlag('wardrobe');
  // R2 (spec §3.8): busy is `sessions[0] != null` and nothing else — the server owns "done", and
  // `SkillBusyCard` itself withholds Cancel once `now >= completesAt` (the sweep may already have
  // applied the row). A `completesAt > now` compare here would call a finishing session idle.
  const skillSession = skillsQuery.data?.sessions[0];
  const cancelSkillSession = skillSession ? () => cancelSkill.mutate(skillSession.id) : undefined;
  const [showSkillsSheet, setShowSkillsSheet] = useState(false);
  const skillsTriggerRef = useRef<HTMLButtonElement>(null);
  // One line, rendered by whichever tier owns the identity (the scene top / the fold / the desk's
  // IdentityPanel) — the data and the sheet stay here (constraint 5).
  const skillsLine = skillCraft ? (
    <SkillsLine
      skills={skillsQuery.data?.skills}
      session={skillSession}
      now={now}
      onOpen={() => setShowSkillsSheet(true)}
      triggerRef={skillsTriggerRef}
    />
  ) : undefined;

  const [playlet, setPlaylet] = useState<PlayletCode | null>(null);
  const [baseVariant, setBaseVariant] = useState<PetBaseVariant>('base');
  const [pokeClass, setPokeClass] = useState<PokeCss | null>(null);
  const [pokeBursts, setPokeBursts] = useState<PokeBurst[]>([]);
  const [pokeGen, setPokeGen] = useState(0);
  const pokeMoodRef = useRef(EMPTY_POKE_MOOD);
  const pokeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pokeGenRef = useRef(0);
  const playletJoyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * The level-up producer — one of the three interrupt-tier events (`docs/DESIGN.md` §21, owner
   * decision D2; the other two are raised shell-side by `shell/useAchievementCelebrations.ts`).
   *
   * A level is monotonic and this diffs it across renders, so the first render of a fresh mount
   * (no seen level yet — `getSeenLevel`, `shell/levelBaseline.ts`) can never fire: arriving
   * already at level 12 is not a level-up. Same shape, and the same guard, the badge/collection
   * producers use.
   *
   * Idempotent under `<StrictMode>`: the seen level is advanced (`markLevelSeen`) in the same run
   * that raises the celebration, so the double-invoked effect's second pass sees
   * `prevLevel === companion.level` and raises nothing.
   *
   * **The baseline outlives the screen (QA 2026-09-23 bug 1).** It used to be a ref, so a level
   * gained while the den was off screen — every battle level-up, every quest step completed on
   * the World hub — was baselined away by the next mount and never celebrated anywhere. It is now
   * the module-level seen-level map in `shell/levelBaseline.ts` (per companion id, shared with the
   * victory card): the same "module state, not a ref" reasoning `useAchievementCelebrations`
   * records in `docs/DESIGN.md` §21. A full page load still seeds silently, which is right — a boot cannot know what happened while the tab was closed.
   * Never written during render (CLAUDE.md Lesson 1); only inside this effect.
   *
   * **What happened to the toast this used to be.** D2 promotes level-up from the decorate tier to
   * the interrupt tier, so the celebration is now the beat and the toast is no longer the place
   * the news is delivered. The toast survives for the one thing the celebration cannot carry: its
   * "Spend points" shortcut into `AllocateSheet`. `celebrationQueue.ts`'s `Celebration` type has no
   * action field — it is a title, a sub and one dismiss button — and widening it would mean
   * editing the queue, which is out of bounds for this change and would also put a
   * Habitat-specific callback into a shell-level store that deliberately knows nothing about
   * screens. So the toast is now raised ONLY when there are points to spend, i.e. only when it is
   * genuinely an affordance rather than a second copy of the celebration's own text; its copy and
   * its actions are unchanged. (The permanent path is unaffected either way: `LevelRing` renders a
   * `+N` badge that opens the same sheet for as long as the points are unspent.)
   */
  const [levelUpToast, setLevelUpToast] = useState<string | null>(null);
  const requestCelebration = useCelebrationQueue((s) => s.request);
  useEffect(() => {
    const prevLevel = getSeenLevel(companion.id);
    if (prevLevel != null && companion.level > prevLevel) {
      const points = companion.freePoints;
      requestCelebration({
        kind: 'level',
        level: companion.level,
        title: `${companion.name} reached Level ${companion.level}`,
        sub: points > 0
          ? `${points} point${points === 1 ? '' : 's'} to spend`
          : `${companion.name} is getting stronger.`,
      });
      if (points > 0) {
        setLevelUpToast(
          `${companion.name} reached Level ${companion.level}! ${points} point${points === 1 ? '' : 's'} to spend`,
        );
      }
    }
    markLevelSeen(companion.id, companion.level);
  }, [companion.id, companion.level, companion.name, companion.freePoints, requestCelebration]);

  const cue = companion.isActive ? lowCareCue(companion.care, companion.name) : null;
  const onCooldown = companion.care.playReadyAt != null;
  const sheetOpen = showLooksSheet || showCareTray || showAllocateSheet || showEquipmentSheet
    || showDressOverlay || showAbilitiesSheet || showPickerSheet || showSkillsSheet;
  const dockMenusOpen = showLooksSheet || showCareTray || showEquipmentSheet || showDressOverlay
    || showAbilitiesSheet || showPickerSheet || showSkillsSheet;
  const [pokeHold, setPokeHold] = useState(false);
  const wanderBlocked = !companion.isActive || sheetOpen || pokeHold || playlet != null
    || placing != null || foodDrop != null || eatHold || grooming != null || groomHold;

  useEffect(() => {
    if (wanderBlocked) {
      wander.pause();
      return;
    }
    wander.resume();
    return () => wander.pause();
  }, [wanderBlocked, wander.pause, wander.resume]);

  useEffect(() => () => {
    if (pokeTimerRef.current != null) clearTimeout(pokeTimerRef.current);
    if (playletJoyTimerRef.current != null) clearTimeout(playletJoyTimerRef.current);
  }, []);

  useEffect(() => {
    if (companion.isActive) return;
    setGrooming(null);
    setGhostPos(null);
    groomSamplesRef.current = [];
  }, [companion.isActive]);

  async function handleFoodDrop(pos: Point) {
    const item = placingRef.current;
    if (!item || feedingRef.current) return;
    const code = item.code;
    setFoodDrop({ code, pos });
    setPlacing(null);
    setGhostPos(null);
    feedingRef.current = true;
    sfx.petFoodDrop();
    try {
      await wander.goTo(pos, { forceRun: true });
      setEatHold(true);
      setBaseVariant('eat');
      sfx.petMunch();
      window.setTimeout(() => { if (feedingRef.current) sfx.petMunch(); }, 280);
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, EAT_HOLD_MS);
      });
      setBaseVariant('base');
      await careAction.mutateAsync({ action: 'feed', itemCode: code });
      sfx.petYum();
    } catch (err) {
      setFeedError(careItemError(err));
    } finally {
      setFoodDrop(null);
      setEatHold(false);
      feedingRef.current = false;
    }
  }

  function handleCancelPlace() {
    setPlacing(null);
    setGhostPos(null);
  }

  function handleCancelGroom() {
    if (groomBusyRef.current) return;
    setGrooming(null);
    setGhostPos(null);
    groomSamplesRef.current = [];
  }

  function handleGroomPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (!grooming || groomHold || groomBusyRef.current) return;
    const scene = e.currentTarget;
    const pct = clientToStagePct(e.clientX, e.clientY, scene.getBoundingClientRect());
    setGhostPos(pct);
    const actor = scene.querySelector('.habitat-scene-actor');
    const fromPoint = document.elementFromPoint?.(e.clientX, e.clientY) ?? null;
    const over = isOverActor(fromPoint, actor) || isOverActor(e.target, actor);
    if (!over) return;
    appendGroomSample(groomSamplesRef.current, e.clientX, e.clientY, e.timeStamp);
    if (!shouldCompleteGroom(groomSamplesRef.current, true)) return;
    const itemCode = grooming;
    groomBusyRef.current = true;
    groomSamplesRef.current = [];
    setGhostPos(null);
    setGrooming(null);
    setGroomHold(true);
    setBaseVariant('joy');
    // QA 2026-09-23 (battle F12): a finished rinse/lather stage is committed BEFORE the joy dwell,
    // so a tray opened during the dwell already knows the stage advanced — it refuses out-of-order
    // tools by name now, and the soap picked 400 ms after a rinse is not out of order. The final
    // stage still commits only on server success, below.
    const washNext = isWashTool(itemCode) ? advanceWash(wash, itemCode) : null;
    if (washNext && !washNext.done) setWash(washNext);
    void (async () => {
      try {
        await new Promise<void>((resolve) => {
          window.setTimeout(resolve, GROOM_JOY_MS);
        });
        // A wash tool never posts a care action -- D5: the wash touches no care stat. Only the
        // last stage talks to the server, and it clears every stain in one call (§6.3).
        if (washNext) {
          if (!washNext.done) return;
          // Final stage: commit `done` only once the server call actually SUCCEEDS. Fix round 1
          // (review finding, minor-but-soft-lock): committing `next` (done: true) before this
          // await meant a failed request left `wash.done` permanently true, and `advanceWash`
          // early-returns whenever `state.done` -- no remount, no retry, ever. Leaving `wash` at
          // its pre-final value on failure instead retries just the final stage (re-shake the
          // towel), not the whole rinse-lather-dry sequence: `WashController.wash` never records
          // anything stage-specific for the first two stages anyway (it only checks tool
          // ownership and clears every stain in one shot), so nothing is lost by not replaying
          // them, and re-demanding two gestures the player already performed correctly over a
          // transient network blip would be needless friction. A thrown error here propagates to
          // the catch below, which already surfaces it via `setGroomError` -- same channel the
          // groom path's own failures use, no second error surface.
          await washMutation.mutateAsync();
          setWash(initialWash());
          return;
        }
        await mutateCareRef.current({ action: 'groom', itemCode });
      } catch (err) {
        setGroomError(careItemError(err));
      } finally {
        setBaseVariant('base');
        setGroomHold(false);
        groomBusyRef.current = false;
      }
    })();
  }

  // A touch gesture is press -> move -> lift, so without pointer capture the browser is free to
  // stop delivering moves to this element mid-stroke, and the shake dies half-finished. The food
  // layer already solved this (FoodPlaceLayer.startDrag); the groom/wash path never did, which is
  // why the gesture worked under a mouse (which streams moves with no button held, and fires no
  // pointerup at all) and failed under a thumb. Capture the pointer for the whole stroke.
  //
  // The Cancel chip is deliberately exempt: capturing here would retarget its own click to this
  // element and the chip would stop working -- the same pointer-capture retargeting that broke
  // the World map pins (see MapPanZoom's own note).
  function handleGroomPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (!grooming || groomHold || groomBusyRef.current) return;
    if ((e.target as Element).closest('.habitat-groom-cancel')) return;
    groomSamplesRef.current = [];
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* jsdom */ }
  }

  // A lift ends the STROKE, not the arming. It used to clear `grooming`, which meant one failed
  // shake -- the travel-and-reversals threshold, inside the shake window, over the pet -- sent the
  // player back through Feed -> Wash -> tap to try again. On a touchscreen every attempt ends in
  // a lift, so that budget was exactly one try per pick. The tool now stays in hand and only the
  // sample trail resets, so the next press starts a clean shake window. `handleCancelGroom` (the
  // Cancel chip) is the only thing that puts the tool down.
  function handleGroomPointerUp(e: ReactPointerEvent<HTMLDivElement>) {
    if (groomBusyRef.current) return;
    if ((e.target as Element).closest('.habitat-groom-cancel')) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already released */ }
    groomSamplesRef.current = [];
  }

  // The browser can take the pointer away mid-stroke (a system gesture, a scroll claim). Same
  // treatment as a lift: drop the half-built trail, keep the tool in hand. Without this the
  // stale samples would join the NEXT stroke's window and could complete a shake the player
  // never actually performed.
  function handleGroomPointerCancel() {
    if (groomBusyRef.current) return;
    groomSamplesRef.current = [];
  }

  function handlePetActivate() {
    if (playlet || placing || foodDrop || eatHold || grooming || groomHold) return;
    const { next, annoyed } = notePoke(pokeMoodRef.current, Date.now());
    const action = pickPokeAction(next, annoyed);
    pokeMoodRef.current = { ...next, lastCss: action.css };
    wander.pause();
    setPokeHold(true);
    const gen = pokeGenRef.current + 1;
    pokeGenRef.current = gen;
    setPokeGen(gen);
    setPokeClass(action.css);
    setPokeBursts(makePokeBursts(action).map((burst) => ({ ...burst, id: gen * 10 + burst.id })));
    sfx[action.sfx]();
    if (!annoyed && isOnline) careAction.mutate({ action: 'poke' });
    if (pokeTimerRef.current != null) clearTimeout(pokeTimerRef.current);
    pokeTimerRef.current = setTimeout(() => {
      if (pokeGenRef.current !== gen) return;
      pokeTimerRef.current = null;
      setPokeClass(null);
      setPokeBursts([]);
      setPokeHold(false);
    }, action.ms);
  }

  function resumeIfClear() {
    if (!companion.isActive || sheetOpen || pokeHold || placing != null || foodDrop != null
      || eatHold || grooming != null || groomHold) {
      return;
    }
    wander.resume();
  }

  function handlePlayletWin() {
    setPlaylet(null);
    setBaseVariant('joy');
    careAction.mutate({ action: 'play' }, {
      onError: () => setPlayError('Something went wrong — please try again.'),
    });
    resumeIfClear();
    if (playletJoyTimerRef.current != null) clearTimeout(playletJoyTimerRef.current);
    playletJoyTimerRef.current = setTimeout(() => {
      playletJoyTimerRef.current = null;
      setBaseVariant('base');
    }, 1000);
  }

  function handlePlayletQuit() {
    setPlaylet(null);
    resumeIfClear();
  }

  function handlePlay() {
    if (!isOnline || onCooldown || playlet != null) return;
    setPlayError(null);
    wander.pause();
    setPlaylet(pickPlaylet());
  }

  function handleStageUp(stage: string) {
    setStageUpToast(`「${companion.name}」 grew to ${stage}! New slots unlocked`);
  }

  // Fires immediately on "Find a school" (labelled "Replace" before Task 12) -- AbilitySlotsSheet
  // no longer shows its own destructive confirm (fix-wave review finding: it used to, behind a
  // Shells-glyphed "Confirm" that fired nothing at all -- no mutation, nothing destroyed). A
  // respec is a *lesson*, and a lesson only happens at a school, which is the one place that
  // actually knows which ability is being learned, what it costs, and can call `startLesson` for
  // real.
  //
  // SECOND correction (same fix-wave): the first attempt carried the target slot to Training
  // School via `?slot=`, the same way `?element=` already arrives from a land hotspot. That
  // cannot work -- nothing a real player clicks ever produces a URL with BOTH params at once
  // (Habitat doesn't know an element; `hotspotMeta.ts` builds a fresh `?element=` link that drops
  // any incoming `?slot=`). Corrected ruling: this button carries NO slot data any more -- it is a
  // plain route to the World hub ("go pick a school"), with no promise about what happens once
  // there. Choosing which slot a lesson targets is now entirely Training School's own job
  // (`TrainingScreen`'s `slotPicker`, see its doc comment) -- this sheet only views slots, it
  // never decided a respec's outcome to begin with.
  //
  // THIRD correction (Task 12): the visible label "Replace" implied an in-place action, when this
  // handler only ever does a bare `/world` navigate -- the exact same destination the World tab
  // itself already reaches. Relabelled to "Find a school" (AbilitySlotsSheet.tsx's own doc
  // comment) rather than cut outright, since it still saves the player the tap of closing this
  // sheet first. See docs/RULINGS.md "Ability slots and training schools".
  function handleReplaceSlot() {
    setShowAbilitiesSheet(false);
    navigate('/world');
  }

  const playletProps = {
    goTo: wander.goTo,
    pause: wander.pause,
    resume: wander.resume,
    setBaseVariant,
    onWin: handlePlayletWin,
    onQuit: handlePlayletQuit,
  };

  const sheets = (
    <>
      {showAllocateSheet && (
        <AllocateSheet
          companion={companion}
          onClose={() => setShowAllocateSheet(false)}
          restoreFocusRef={allocateTriggerRef}
          variant="overlay"
        />
      )}
      {showEquipmentSheet && (
        <EquipmentSheet
          companion={companion}
          onClose={() => setShowEquipmentSheet(false)}
          restoreFocusRef={equipTriggerRef}
          variant="overlay"
        />
      )}
      {showAbilitiesSheet && slotsQuery.data && (
        <AbilitySlotsSheet
          slots={slotsQuery.data.slots}
          onReplace={handleReplaceSlot}
          onClose={() => setShowAbilitiesSheet(false)}
          restoreFocusRef={abilitiesTriggerRef}
          variant="overlay"
        />
      )}
      {showAbilitiesSheet && slotsQuery.isError && (
        <BottomSheet
          open
          onOpenChange={(next) => { if (!next) setShowAbilitiesSheet(false); }}
          contentClassName="ability-slots-sheet"
          restoreFocusRef={abilitiesTriggerRef}
          variant="overlay"
        >
          <Dialog.Title className="sr-only">Ability slots</Dialog.Title>
          <div className="retry-card">
            <p>Couldn&rsquo;t reach Project Pets.</p>
            <button type="button" className="btn-primary" onClick={() => slotsQuery.refetch()}>Try again</button>
          </div>
        </BottomSheet>
      )}
      {showPickerSheet && (
        <CompanionPickerSheet
          activeId={companion.id}
          onClose={() => setShowPickerSheet(false)}
          restoreFocusRef={pickerTriggerRef}
        />
      )}
      {skillCraft && (
        <SkillsSheet
          open={showSkillsSheet}
          onOpenChange={(next) => { if (!next) setShowSkillsSheet(false); }}
          companionName={companion.name}
          skills={skillsQuery.data?.skills}
          session={skillSession}
          now={now}
          onCancel={cancelSkillSession}
          cancelPending={cancelSkill.isPending}
          triggerRef={skillsTriggerRef}
        />
      )}
      {showLooksSheet && (
        <LooksSheet
          companionId={companion.id}
          onClose={() => setShowLooksSheet(false)}
          restoreFocusRef={looksTriggerRef}
          variant="overlay"
        />
      )}
      {showCareTray && (
        <CareTray
          companion={companion}
          onClose={() => setShowCareTray(false)}
          onPlaceFood={(item) => {
            setGrooming(null);
            setGhostPos(null);
            setPlacing(item);
          }}
          onGroomPick={(itemCode) => {
            setPlacing(null);
            groomSamplesRef.current = [];
            setGhostPos(wander.pos);
            setGrooming(itemCode);
          }}
          onStageUp={handleStageUp}
          washStageIndex={wash.stageIndex}
          restoreFocusRef={careTrayTab === 'groom' ? groomTriggerRef : feedTriggerRef}
          variant="overlay"
          initialTab={careTrayTab}
        />
      )}
      {showDressOverlay && (
        <BottomSheet
          open
          onOpenChange={(next) => { if (!next) setShowDressOverlay(false); }}
          contentClassName="habitat-dress-overlay"
          restoreFocusRef={dressTriggerRef}
          variant="overlay"
          lockDismiss
          onLockedEscape={() => dressCloseRef.current?.()}
        >
          <Dialog.Title className="sr-only">Dress {companion.name}</Dialog.Title>
          <Dialog.Description className="sr-only">
            Change this companion&rsquo;s outfit without leaving the den.
          </Dialog.Description>
          <WardrobeScreenForCompanion
            companionId={companion.id}
            onClose={() => setShowDressOverlay(false)}
            closeRef={dressCloseRef}
          />
        </BottomSheet>
      )}
    </>
  );

  if (!companion.isActive) {
    return (
      <div className="habitat-scene-inactive">
        <h2>{companion.name}</h2>
        <button
          type="button"
          className="btn-quiet"
          disabled={!isOnline || setActive.isPending}
          onClick={() => setActive.mutate(companion.id)}
        >
          {setActive.isPending ? 'Setting…' : 'Set active'}
        </button>
        {sheets}
      </div>
    );
  }

  // The identity block and the dock, lifted out of `HabitatScene`'s `top`/`floor` props (Task 7,
  // spec §4.5 Habitat) so the tier can place them: the phone keeps both on the scene exactly as
  // before; the tablet moves the identity into the Studio fold; the desk puts the identity in the
  // left panel (studio3) or the fold (short/studio2) and replaces the dock with the Dashboard.
  //
  // SCREENS.md /habitat "identity-bar": ONE row — the ring, and beside it a two-line
  // stack of [name · species · stage · Thriving] over the three care meters.
  // Free points ride the level ring rather than a full-width pill — the ring is
  // already the level affordance and clears §11.3's touch floor on its own.
  //
  // WHY THE METERS MOVED INSIDE THE STACK (2026-08-20). They used to be a second row
  // of the panel, under this one. That made the panel `ring-row + gap + meter-row`
  // tall, and since the ring is --sp-7 it set the first row's height on its own —
  // so compacting the TEXT (which is what "species · stage on its own line" was)
  // bought nothing at all: the row stayed as tall as the ring either way. Putting the
  // meters in the stack lets them share the ring's height instead of adding to it,
  // which is the only edit here that actually gives the den back any pixels.
  // `showThriving={false}` because the badge sits on the name line — the gallery
  // keeps the default in-row badge.
  const identity = (
    <div className="habitat-scene-identity">
      <div className="habitat-card-title-row">
        <LevelRing
          level={companion.level}
          xp={companion.xp}
          xpToNext={companion.xpToNext}
          freePoints={companion.freePoints}
          onAllocate={() => setShowAllocateSheet(true)}
          allocateRef={allocateTriggerRef}
        />
        <div className="habitat-name-stack">
          {/* A div, not a p: it contains an h2, and the HTML parser auto-closes a <p>
              before flow content, which would silently reparent the heading. */}
          {/* On a multi-companion account the name line is the SWITCHER (SCREENS.md
              /habitat "companion picker"): a button carrying the h2, the subtitle and a
              chevron, opening CompanionPickerSheet. A one-companion account keeps the
              plain line — a button that opens a list of one would promise a choice that
              does not exist. The Thriving badge stays OUTSIDE the button either way: it
              is a status, not part of the control's name. */}
          <div className="habitat-name-line">
            {switchable ? (
              <button
                ref={pickerTriggerRef}
                type="button"
                className="habitat-name-btn"
                aria-haspopup="dialog"
                onClick={() => setShowPickerSheet(true)}
              >
                <h2>{companion.name}</h2>
                <span className="habitat-name-sub">{companion.speciesName} · {companion.stage}</span>
                <span className="habitat-name-chevron" aria-hidden="true" />
              </button>
            ) : (
              <>
                <h2>{companion.name}</h2>
                <span className="habitat-name-sub">{companion.speciesName} · {companion.stage}</span>
              </>
            )}
            {companion.care.thriving && (
              <span className="badge-thriving" aria-label="Thriving: +10% XP and better finds from battles, gathering and gifts">
                Thriving · +10%
              </span>
            )}
          </div>
          {/* On a studio tier the Dashboard (the right panel) owns the three meters — spec §4.5
              lists the left panel as identity only — so the same three `role="meter"`s are never
              on screen twice. Phone and tablet keep them here, exactly as before. */}
          {!studio && <CareMeters care={companion.care} companionName={companion.name} showThriving={false} />}
          {!three && skillsLine}
        </div>
      </div>
    </div>
  );
  const playErrorLine = playError && <p className="field-error" role="alert">{playError}</p>;
  const dock = (
    <>
      <button
        ref={feedTriggerRef}
        type="button"
        className="habitat-dock-btn"
        data-dock="feed"
        disabled={!isOnline}
        onClick={() => { setCareTrayTab('food'); setShowCareTray(true); }}
      >
        <span className="habitat-dock-glyph" aria-hidden="true"><HabitatDockIcon id="feed" /></span>
        <span className="habitat-dock-label">Feed</span>
      </button>
      <button
        type="button"
        className="habitat-dock-btn"
        data-dock="play"
        // SCREENS.md /habitat "play-cooldown" puts the remaining time in the accessible
        // name, but a native `disabled` button is removed from the tab order — so the one
        // channel carrying that information was unreachable by the assistive-tech users it
        // was written for. `aria-disabled` keeps it focusable and announced; `handlePlay`
        // returns early rather than relying on the browser to swallow the click. The other
        // blockers stay natively disabled — they are transient, not informational.
        disabled={!isOnline || careAction.isPending || playlet != null}
        aria-disabled={onCooldown || undefined}
        aria-label={onCooldown
          ? `Play again in ${formatEta(companion.care.playReadyAt as string)}`
          : careAction.isPending ? 'Playing…' : undefined}
        onClick={handlePlay}
      >
        <span className="habitat-dock-glyph" aria-hidden="true"><HabitatDockIcon id="play" /></span>
        <span className="habitat-dock-label">Play</span>
        {/* §11.8 "never colour alone": the cooldown was previously conveyed only by a
            dimmed button. The remaining time is now visible text as well. */}
        {onCooldown && (
          <span className="habitat-dock-cooldown" aria-hidden="true">
            {formatEta(companion.care.playReadyAt as string)}
          </span>
        )}
      </button>
      {wardrobeOn && (
        <button
          ref={dressTriggerRef}
          type="button"
          className="habitat-dock-btn"
          data-dock="dress"
          onClick={() => setShowDressOverlay(true)}
        >
          <span className="habitat-dock-glyph" aria-hidden="true"><HabitatDockIcon id="dress" /></span>
          <span className="habitat-dock-label">Dress</span>
        </button>
      )}
      <button
        ref={equipTriggerRef}
        type="button"
        className="habitat-dock-btn"
        data-dock="equip"
        disabled={!isOnline}
        onClick={() => setShowEquipmentSheet(true)}
      >
        <span className="habitat-dock-glyph" aria-hidden="true"><HabitatDockIcon id="equip" /></span>
        <span className="habitat-dock-label">Equip</span>
      </button>
      {wardrobeOn && (
        <button
          ref={looksTriggerRef}
          type="button"
          className="habitat-dock-btn"
          data-dock="looks"
          onClick={() => setShowLooksSheet(true)}
        >
          <span className="habitat-dock-glyph" aria-hidden="true"><HabitatDockIcon id="looks" /></span>
          <span className="habitat-dock-label">Looks</span>
        </button>
      )}
      <button
        ref={abilitiesTriggerRef}
        type="button"
        className="habitat-dock-btn"
        data-dock="abilities"
        disabled={!isOnline}
        onClick={() => setShowAbilitiesSheet(true)}
      >
        <span className="habitat-dock-glyph" aria-hidden="true"><HabitatDockIcon id="abilities" /></span>
        <span className="habitat-dock-label">Abilities</span>
      </button>
    </>
  );
  const dashboard = studio ? (
    <>
      <Dashboard
        companion={companion}
        disabled={!isOnline}
        // The screen's own verdicts, never re-derived by the Dashboard (fix round 1, finding 2):
        // the same `onCooldown` and the same transient Play blockers the phone dock honours.
        onCooldown={onCooldown}
        playBlocked={careAction.isPending || playlet != null}
        wardrobeOn={wardrobeOn}
        onFeed={() => { setCareTrayTab('food'); setShowCareTray(true); }}
        onPlay={handlePlay}
        onGroom={() => { setCareTrayTab('groom'); setShowCareTray(true); }}
        onDress={() => setShowDressOverlay(true)}
        onEquip={() => setShowEquipmentSheet(true)}
        onLooks={() => setShowLooksSheet(true)}
        onAbilities={() => setShowAbilitiesSheet(true)}
        // The sheets' `restoreFocusRef`s: on the desk the dock is not rendered, so these refs
        // attach to the Dashboard's buttons instead (finding 1) — same ref objects, one owner
        // per tier, so a closing sheet returns focus to the control that opened it.
        feedRef={feedTriggerRef}
        groomRef={groomTriggerRef}
        dressRef={dressTriggerRef}
        equipRef={equipTriggerRef}
        looksRef={looksTriggerRef}
        abilitiesRef={abilitiesTriggerRef}
      />
      {playErrorLine}
      <TodayCard />
      <NextStepCard />
    </>
  ) : undefined;

  return (
    <Studio
      className="studio-habitat"
      leftLabel="Companion"
      rightLabel="Dashboard"
      left={three ? (
        <div className="habitat-identity-panel">
          <IdentityPanel
            companion={companion}
            companions={companions}
            slots={slotsQuery.data?.slots}
            identity={identity}
            onPickCompanion={() => setShowPickerSheet(true)}
            onAbilities={() => setShowAbilitiesSheet(true)}
            onAllocate={() => setShowAllocateSheet(true)}
            skillsLine={skillsLine}
          />
        </div>
      ) : undefined}
      fold={!three && tier !== 'phone' ? <div className="habitat-identity-fold">{identity}{tier === 'tablet' && <NextStepCard />}{playErrorLine}</div> : undefined}
      right={dashboard}
    >
      <HabitatScene
        className={dockMenusOpen ? 'habitat-menus-open' : undefined}
        // D13 + D10: `HabitatScene` reads `companion.stains` unconditionally — gate it here, at
        // the one call site, rather than adding an age-gate/gore-toggle concept to a shared
        // presentational component. `stainsOn` folds in both the viewer's age band and the
        // battle-effects preference (server-backed per #14.2 / R1; see its own definition above).
        companion={stainsOn ? companion : { ...companion, stains: [] }}
        // Task 16: fades the overlay per wash stage (§6.2) — visual only, the stain RECORD only
        // changes once, on the final stage's server call (see `handleGroomPointerMove` above).
        stainOpacity={stainFadeFor(wash)}
        wardrobe={wardrobe.isSuccess && wardrobe.data ? {
          speciesCode: wardrobe.data.speciesCode,
          stage: wardrobe.data.stage,
        } : null}
        baseVariant={baseVariant}
        pos={wander.pos}
        facing={wander.facing}
        motion={wander.motion}
        dull={Boolean(cue)}
        // The Dress overlay has its own live dressed pet; nothing sees this one under it.
        paused={showDressOverlay}
        pokeClass={pokeClass}
        pokeBursts={pokeBursts}
        pokeGen={pokeGen}
        petDisabled={playlet != null || placing != null || foodDrop != null || eatHold || grooming != null || groomHold}
        onPetActivate={handlePetActivate}
        onPointerDown={grooming && !groomHold ? handleGroomPointerDown : undefined}
        onPointerMove={grooming && !groomHold ? handleGroomPointerMove : undefined}
        onPointerUp={grooming && !groomHold ? handleGroomPointerUp : undefined}
        onPointerCancel={grooming && !groomHold ? handleGroomPointerCancel : undefined}
        top={studio || tier === 'tablet' ? null : <>{identity}<NextStepCard />{playErrorLine}</>}
        floor={studio ? null : dock}
        extras={(
          <>
            {playlet === 'fetch' && <FetchPlaylet {...playletProps} />}
            {playlet === 'bubbles' && <BubblesPlaylet {...playletProps} />}
            {playlet === 'hide_seek' && <HideSeekPlaylet {...playletProps} />}
            {(placing || foodDrop) && (
              <FoodPlaceLayer
                item={placing}
                drop={foodDrop}
                eating={eatHold}
                onDrop={handleFoodDrop}
                onCancel={handleCancelPlace}
              />
            )}
            {grooming && (
              <div className="habitat-groom-layer">
                <p className="form-note habitat-groom-hint">Drag and shake over the pet</p>
                <button
                  type="button"
                  className="chip habitat-groom-cancel"
                  onPointerUp={(e) => e.stopPropagation()}
                  onClick={handleCancelGroom}
                >
                  Cancel
                </button>
                {ghostPos && (
                  <img
                    className="habitat-groom-ghost"
                    src={itemIconPath(grooming)}
                    alt=""
                    style={{ left: `${ghostPos.x}%`, top: `${ghostPos.y}%` }}
                  />
                )}
              </div>
            )}
            {feedError && (
              <div className="toast" role="alert">
                <span>{feedError}</span>
                <button type="button" className="toast-dismiss" onClick={() => setFeedError(null)}>Dismiss</button>
              </div>
            )}
            {groomError && (
              <div className="toast" role="alert">
                <span>{groomError}</span>
                <button type="button" className="toast-dismiss" onClick={() => setGroomError(null)}>Dismiss</button>
              </div>
            )}
            {stageUpToast && (
              <div className="toast" role="alert">
                <span>{stageUpToast}</span>
                <div className="toast-actions">
                  <Link to={`/wardrobe/${companion.id}`} className="toast-dismiss">Open wardrobe</Link>
                  <button type="button" className="toast-dismiss" onClick={() => setStageUpToast(null)}>Dismiss</button>
                </div>
              </div>
            )}
            {levelUpToast && (
              <div className="toast" role="alert">
                <span>{levelUpToast}</span>
                <div className="toast-actions">
                  <button
                    type="button"
                    className="toast-dismiss"
                    onClick={() => { setLevelUpToast(null); setShowAllocateSheet(true); }}
                  >
                    Spend points
                  </button>
                  <button type="button" className="toast-dismiss" onClick={() => setLevelUpToast(null)}>Dismiss</button>
                </div>
              </div>
            )}
          </>
        )}
      />
      {sheets}
    </Studio>
  );
}

export function HabitatScreen() {
  const isOnline = useOnline();
  const q = useCompanions();

  if (q.isLoading) return <HabitatSkeleton />;

  if (q.isError) {
    if (!isOnline) return <EmptyHabitat />;
    return (
      <main className="habitat">
        <div className="retry-card">
          <p>Couldn&rsquo;t reach Project Pets.</p>
          <button className="btn-primary" onClick={() => q.refetch()}>Try again</button>
        </div>
      </main>
    );
  }

  const companions = q.data ?? [];
  if (companions.length === 0) return <EmptyHabitat />;

  const active = companions.filter((c) => c.isActive);
  const inactive = companions.filter((c) => !c.isActive);

  // Inactive companions are not in this room (SCREENS.md /habitat). They used to render as a
  // "name + Set active" strip each under the scene, which the full-bleed layout pushed under the
  // floating dock where nothing could reach them; the switcher is now the identity bar's name
  // (CompanionPickerSheet). The strips survive ONLY as the fallback for a list with no active
  // companion at all — a state the server does not produce, but one that must not render an
  // empty den with no way out.
  const shown = active.length > 0 ? active : inactive;

  return (
    <main className="habitat">
      <h1 className="sr-only">Habitat</h1>
      {shown.map((c) => <CompanionCard key={c.id} companion={c} companions={companions} switchable={companions.length > 1} />)}
    </main>
  );
}
